// Moodboard preview baker (Jun 18 — Recipes-as-Templates Phase 2).
// Curator picks N recipes + uploads one product → backend renders each
// recipe verbatim → upload to <vibe>/previews/<id>.png in the moodboard-
// previews bucket → sidecar gains preview_path/preview_url/preview_
// generated_at/preview_recipe_version_at.
//
// Workflow: filter to recipes that fit a product type (e.g. footwear) →
// upload a representative product (e.g. amber dropper bottle for
// "light-through-glass" recipes) → tick checkboxes → Generate. Iterate
// across product types in separate batches.
//
// DEV-only via import.meta.env.DEV gate on the route.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, getAuthHeaders } from '@/lib/api';
import type { SocialPostSlots } from '@/lib/api';
import JsonSocialPost from '@/pages/app/components/social-designs/json-templates/JsonSocialPost';
import type { JsonTemplate, TextElement } from '@/pages/app/components/social-designs/json-templates/types';
import { PLACEHOLDER_SLOTS } from '@/lib/socialPostPipeline';
import {
  getTextTemplateList,
  getTextTemplate,
} from '@/pages/app/components/social-designs/text-templates/registry';

// ─── Type contracts mirror /moodboard-builder ────────────────────────
type Vibe = 'Premium' | 'Warm & lifestyle' | 'Playful' | 'Clean & minimal' | 'Editorial';
const VIBES: Vibe[] = ['Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial'];

type ProductType =
  | 'skincare' | 'haircare' | 'apparel' | 'footwear' | 'accessory'
  | 'consumable'
  | 'tea-coffee' | 'sports-drink' | 'workout-supplement'  // Jul 6 — consumable split
  | 'tech' | 'home'
  // Jul 7 — distinct apparel / footwear / beauty / supplement sub-types (strict).
  | 'shirts' | 'pants' | 'dresses' | 'activewear' | 'coats'
  | 'sneakers' | 'boots' | 'flats' | 'heels' | 'sandals'  // Jul 20
  | 'makeup' | 'face-makeup' | 'eye-makeup' | 'lip-makeup' | 'fragrance' | 'supplement'  // Jul 28
  | 'wearable'    // ← legacy
  | 'universal';  // ← deprecated
const PRODUCT_TYPES: ProductType[] = [
  'skincare', 'haircare', 'apparel', 'footwear', 'accessory',
  'consumable', 'tea-coffee', 'sports-drink', 'workout-supplement',
  'tech', 'home',
  'shirts', 'pants', 'dresses', 'activewear', 'coats',
  'sneakers', 'boots', 'flats', 'heels', 'sandals',
  'makeup', 'face-makeup', 'eye-makeup', 'lip-makeup', 'fragrance', 'supplement',
];
const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  skincare: 'Skincare', haircare: 'Haircare', apparel: 'Apparel', footwear: 'Footwear', accessory: 'Accessory',
  consumable: 'Consumable',
  'tea-coffee': 'Tea & Coffee', 'sports-drink': 'Sports / Energy', 'workout-supplement': 'Workout Supp.',
  tech: 'Tech', home: 'Home',
  shirts: 'Shirts', pants: 'Pants', dresses: 'Dresses', activewear: 'Activewear', coats: 'Coats & Jackets',
  sneakers: 'Sneakers', boots: 'Boots', flats: 'Flats', heels: 'Heels', sandals: 'Sandals',
  makeup: 'Makeup', 'face-makeup': 'Face Makeup', 'eye-makeup': 'Eye Makeup', 'lip-makeup': 'Lip Makeup',
  fragrance: 'Fragrance', supplement: 'Vitamins & Supp.',
  wearable: 'Wearable (legacy)', universal: 'Universal (deprecated)',
};

type MoodboardRecord = {
  id: string;
  vibe: Vibe;
  vibe_slug: string;
  tags: string[];
  style_summary: string;
  image_url: string;
  ready_for_production?: boolean;
  product_types?: ProductType[];
  approved_recipe?: Record<string, unknown>;
  approved_recipe_updated_at?: string;
  preview_url?: string;
  preview_path?: string;
  preview_generated_at?: string;
  preview_recipe_version_at?: string;
  // Curator routing flag (Jun 23) — surfaced + togglable from this page so
  // tagging happens against the recipe's actual moodboard image, not the
  // preview render (the preview can carry baked copy even when the recipe
  // is text-free at the spec level).
  is_product_shot?: boolean;
};

type ListResponse = { vibes: Vibe[]; groups: Record<Vibe, MoodboardRecord[]> };

// ─── Constants ────────────────────────────────────────────────────────
const HARD_CAP = 30;
const SOFT_WARN = 20;
const COST_PER_RENDER = 0.04;
const PRODUCT_SESSION_KEY = 'moodboard-previews:lastProduct';

// Status badge per tile (derived from preview_recipe_version_at vs approved_recipe_updated_at).
type PreviewStatus = 'fresh' | 'stale' | 'missing';
function deriveStatus(rec: MoodboardRecord): PreviewStatus {
  if (!rec.preview_url) return 'missing';
  const recipeUpdated = rec.approved_recipe_updated_at;
  const previewVersion = rec.preview_recipe_version_at;
  if (recipeUpdated && previewVersion && recipeUpdated > previewVersion) return 'stale';
  return 'fresh';
}

// ─── Text-overlay dev helpers (Jun 27 — fusion text-template overlay) ──
// The fusion render produces a TEXT-FREE scene; the curated text template
// composites on top. Two pieces the production vision-picker will own:
//   1. tone — light vs dark text, picked from scene luminance so copy
//      stays legible regardless of what the scene rendered.
//   2. (later) zone — which calm region the text lands in. v1 uses the
//      template's authored positions verbatim; the picker can translate.

type OverlayTone = 'auto' | 'light' | 'dark';

/** Mean perceptual luminance (0=black … 1=white) of a base64 PNG,
 *  sampled from a tiny downscaled canvas. Cheap; used to auto-pick the
 *  text tone. Resolves 0.5 (neutral) if the image can't be read. */
function sampleSceneLuminance(imageB64: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const N = 32;
        const c = document.createElement('canvas');
        c.width = N;
        c.height = N;
        const ctx = c.getContext('2d');
        if (!ctx) return resolve(0.5);
        ctx.drawImage(img, 0, 0, N, N);
        const { data } = ctx.getImageData(0, 0, N, N);
        let sum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          // Rec. 601 luma, normalized 0..1.
          sum += (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
          count++;
        }
        resolve(count ? sum / count : 0.5);
      } catch {
        resolve(0.5);
      }
    };
    img.onerror = () => resolve(0.5);
    img.src = `data:image/png;base64,${imageB64}`;
  });
}

/** Clone a text template with text-element colors flipped for the chosen
 *  tone. Buttons/shapes keep their authored colors (a button with its own
 *  bg is already scene-independent). 'dark' = authored colors verbatim. */
/** Compact summary of where a template's TEXT blocks sit (for the vision
 *  picker prompt), e.g. "headline: top-left; subhead: bottom-center". */
function describeTemplateLayout(tpl: JsonTemplate): string {
  const W = tpl.canvas?.width || 1024;
  const H = tpl.canvas?.height || 1024;
  const parts: string[] = [];
  for (const el of tpl.elements) {
    if (el.type !== 'text') continue;
    const cx = (el.x + el.width / 2) / W;
    const cy = (el.y + (el.height ?? 40) / 2) / H;
    const h = cx < 0.38 ? 'left' : cx > 0.62 ? 'right' : 'center';
    const v = cy < 0.33 ? 'top' : cy > 0.66 ? 'bottom' : 'middle';
    parts.push(`${el.slotKey || 'text'}: ${v}-${h}`);
  }
  return parts.join('; ') || 'text positions unknown';
}

function applyTextTone(template: JsonTemplate, tone: 'light' | 'dark'): JsonTemplate {
  // Force BOTH tones — templates author white text for dark scenes, so a
  // bright scene needs a real dark override (not "keep authored" = white).
  const color = tone === 'light' ? '#ffffff' : '#1a1a1a';
  return {
    ...template,
    elements: template.elements.map((el) =>
      el.type === 'text' ? ({ ...el, color } as TextElement) : el,
    ),
  };
}

export default function MoodboardPreviewsPage() {
  // ─── Data fetching ────────────────────────────────────────────────
  const [library, setLibrary] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/list`, { headers });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      setLibrary((await resp.json()) as ListResponse);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void fetchLibrary(); }, [fetchLibrary]);

  // ─── Filters (persist via URL? not for now — in-memory only) ───────
  const [vibeFilter, setVibeFilter] = useState<Vibe | null>(null);
  const [productTypeFilter, setProductTypeFilter] = useState<ProductType | 'untagged' | null>(null);
  const [statusFilter, setStatusFilter] = useState<PreviewStatus | null>(null);
  // Jun 23 — narrow the grid to tagged product-shot recipes so the curator
  // can audit them quickly (verify each one looks text-free, untag mistakes).
  const [productShotOnly, setProductShotOnly] = useState(false);
  // Only show approved recipes — un-approved have no recipe to render.
  const [approvedOnly, setApprovedOnly] = useState(true);

  // ─── Product (the thing being rendered into each recipe) ──────────
  const [productFile, setProductFile] = useState<File | null>(null);
  const [productPreviewUrl, setProductPreviewUrl] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [brandName, setBrandName] = useState('');

  // Restore last-uploaded product from sessionStorage on mount.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PRODUCT_SESSION_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw) as { name?: string; brand?: string; dataUrl?: string; fileName?: string };
      if (cached.dataUrl && cached.fileName) {
        // Reconstruct File from data URL.
        fetch(cached.dataUrl).then((r) => r.blob()).then((blob) => {
          setProductFile(new File([blob], cached.fileName!, { type: blob.type || 'image/png' }));
          setProductPreviewUrl(cached.dataUrl!);
        }).catch(() => undefined);
      }
      if (cached.name) setProductName(cached.name);
      if (cached.brand) setBrandName(cached.brand);
    } catch { /* ignore */ }
  }, []);

  const handleProductFile = useCallback((file: File | null) => {
    setProductFile(file);
    if (!file) {
      setProductPreviewUrl(null);
      try { sessionStorage.removeItem(PRODUCT_SESSION_KEY); } catch { /* ignore */ }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setProductPreviewUrl(dataUrl);
      try {
        sessionStorage.setItem(PRODUCT_SESSION_KEY, JSON.stringify({
          name: productName, brand: brandName, dataUrl, fileName: file.name,
        }));
      } catch { /* ignore — sessionStorage quota or disabled */ }
    };
    reader.readAsDataURL(file);
  }, [productName, brandName]);

  // Update sessionStorage when name/brand change (without re-encoding file).
  useEffect(() => {
    if (!productPreviewUrl || !productFile) return;
    try {
      sessionStorage.setItem(PRODUCT_SESSION_KEY, JSON.stringify({
        name: productName, brand: brandName, dataUrl: productPreviewUrl, fileName: productFile.name,
      }));
    } catch { /* ignore */ }
  }, [productName, brandName, productPreviewUrl, productFile]);

  // ─── Filtered view ────────────────────────────────────────────────
  const matchesProductType = useCallback((rec: MoodboardRecord, target: ProductType | 'untagged' | null): boolean => {
    if (!target) return true;
    const types = rec.product_types ?? [];
    // 'untagged' surfaces the STRANDED recipes — no product_types, so the
    // strict matcher (and the synth) skip them entirely. Without this chip
    // they're invisible under every type filter, so a curator can't find
    // them to tag. This is why "new recipes never show in the synth".
    if (target === 'untagged') return types.length === 0;
    // Strict — mirror backend picker: untagged invisible to a type filter,
    // wearable subsumes apparel/footwear/accessory, universal deprecated.
    if (types.length === 0) return false;
    if (types.includes(target)) return true;
    if (['apparel', 'footwear', 'accessory'].includes(target) && types.includes('wearable')) return true;
    return false;
  }, []);

  const filteredFlat = useMemo(() => {
    if (!library) return [];
    const flat: MoodboardRecord[] = [];
    for (const vibe of VIBES) {
      if (vibeFilter && vibe !== vibeFilter) continue;
      const rows = library.groups[vibe] ?? [];
      for (const rec of rows) {
        if (approvedOnly && !rec.ready_for_production) continue;
        if (!matchesProductType(rec, productTypeFilter)) continue;
        if (statusFilter && deriveStatus(rec) !== statusFilter) continue;
        if (productShotOnly && !rec.is_product_shot) continue;
        flat.push(rec);
      }
    }
    return flat;
  }, [library, vibeFilter, productTypeFilter, statusFilter, approvedOnly, productShotOnly, matchesProductType]);

  // ─── Selection ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAllInView = useCallback(() => {
    setSelectedIds(new Set(filteredFlat.map((r) => r.id)));
  }, [filteredFlat]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Toggle is_product_shot on a recipe (Jun 23). Same routing endpoint
  // /moodboard-builder uses — patches the GCS sidecar + invalidates the
  // picker cache. We tag from here because the rendered preview can carry
  // baked copy even when the recipe spec is text-free; tagging by the
  // moodboard image + spec metadata is the source of truth.
  const setProductShot = useCallback(
    async (record: MoodboardRecord, value: boolean) => {
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(
          `${API_BASE}/api/dev/moodboard/${record.vibe_slug}/${record.id}/routing`,
          { method: 'POST', headers, body: JSON.stringify({ is_product_shot: value }) },
        );
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`${resp.status}: ${text || resp.statusText}`);
        }
        const data = (await resp.json()) as { is_product_shot: boolean };
        setLibrary((prev) => {
          if (!prev) return prev;
          const next: ListResponse = { vibes: prev.vibes, groups: { ...prev.groups } };
          for (const v of prev.vibes) {
            const list = next.groups[v] ?? [];
            const idx = list.findIndex((r) => r.id === record.id);
            if (idx < 0) continue;
            next.groups[v] = [
              ...list.slice(0, idx),
              { ...list[idx], is_product_shot: data.is_product_shot },
              ...list.slice(idx + 1),
            ];
            break;
          }
          return next;
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        alert(`Tag update failed: ${msg}`);
      }
    },
    [],
  );

  // Drop selection state for tiles no longer in view (filter changed away).
  useEffect(() => {
    setSelectedIds((prev) => {
      const visibleIds = new Set(filteredFlat.map((r) => r.id));
      const next = new Set<string>();
      for (const id of prev) if (visibleIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [filteredFlat]);

  // ─── Per-tile state during generation ─────────────────────────────
  const [renderingIds, setRenderingIds] = useState<Set<string>>(new Set());
  const [tileErrors, setTileErrors] = useState<Record<string, string>>({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchSummary, setBatchSummary] = useState<{ succeeded: number; failed: number; cost: number } | null>(null);

  const handleGenerate = useCallback(async () => {
    if (!productFile) {
      alert('Upload a product image first.');
      return;
    }
    if (selectedIds.size === 0) {
      alert('Select at least one recipe.');
      return;
    }
    if (selectedIds.size > HARD_CAP) {
      alert(`Too many selected (${selectedIds.size}). Hard cap is ${HARD_CAP} per batch — split into smaller batches.`);
      return;
    }
    if (selectedIds.size > SOFT_WARN) {
      const ok = window.confirm(
        `You're about to render ${selectedIds.size} previews (~$${(selectedIds.size * COST_PER_RENDER).toFixed(2)}). Continue?`,
      );
      if (!ok) return;
    }

    // Build the request body.
    const recipes = filteredFlat
      .filter((r) => selectedIds.has(r.id))
      .map((r) => ({ vibe_slug: r.vibe_slug, id: r.id }));
    if (recipes.length === 0) return;

    // File → base64.
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read product file.'));
      reader.readAsDataURL(productFile);
    });
    const b64 = dataUrl.split(',', 2)[1] ?? '';
    if (!b64) {
      alert('Could not encode product image.');
      return;
    }

    setBatchRunning(true);
    setBatchSummary(null);
    setTileErrors({});
    setRenderingIds(new Set(recipes.map((r) => r.id)));

    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/moodboard/generate-previews-batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipes,
          product_image_b64: b64,
          product_mime: productFile.type || 'image/png',
          product_name: productName.trim() || 'the product',
          brand_name: brandName.trim(),
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = await resp.json() as {
        results: Array<{
          vibe_slug: string;
          id: string;
          preview_url?: string;
          preview_path?: string;
          preview_generated_at?: string;
          error?: string;
        }>;
        succeeded: number;
        failed: number;
        total_cost_estimate: number;
      };

      // Patch library state in place — for each successful result, set
      // preview_url + preview_generated_at + preview_recipe_version_at.
      setLibrary((prev) => {
        if (!prev) return prev;
        const next: ListResponse = { vibes: prev.vibes, groups: { ...prev.groups } };
        for (const result of data.results) {
          if (!result.preview_url) continue;
          // Find the record's vibe via its current group.
          for (const vibe of VIBES) {
            const list = next.groups[vibe] ?? [];
            const idx = list.findIndex((r) => r.id === result.id);
            if (idx < 0) continue;
            const updated: MoodboardRecord = {
              ...list[idx],
              preview_url: result.preview_url,
              preview_path: result.preview_path,
              preview_generated_at: result.preview_generated_at,
              preview_recipe_version_at: list[idx].approved_recipe_updated_at,
            };
            next.groups[vibe] = [...list.slice(0, idx), updated, ...list.slice(idx + 1)];
            break;
          }
        }
        return next;
      });

      // Tile-level errors.
      const errs: Record<string, string> = {};
      for (const r of data.results) {
        if (r.error) errs[r.id] = r.error;
      }
      setTileErrors(errs);

      setBatchSummary({
        succeeded: data.succeeded,
        failed: data.failed,
        cost: data.total_cost_estimate,
      });
      // Clear selection on completion (curator's done with this set).
      clearSelection();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(`Batch failed: ${msg}`);
    } finally {
      setBatchRunning(false);
      setRenderingIds(new Set());
    }
  }, [productFile, selectedIds, filteredFlat, productName, brandName, clearSelection]);

  const selectedCount = selectedIds.size;
  const estimatedCost = (selectedCount * COST_PER_RENDER).toFixed(2);

  // ─── FLUX fusion test (Jun 26) ─────────────────────────────────────
  // Variance-via-fusion hypothesis: send N recipe previews + product to
  // FLUX with no recipe spec — just multi-image composition. If it
  // produces coherent blends + per-render uniqueness, the production
  // variance system (cycle math + ADD-CREATIVE-VARIATION directive) is
  // deletable. Self-contained panel; doesn't interfere with the existing
  // "Bake N previews" flow above which targets a different endpoint.
  const [fusionOpen, setFusionOpen] = useState(false);
  // Provider switch — default OpenAI (Jun 26 after first FLUX tests didn't
  // preserve the product subject). Same prompt + ref order works on both;
  // OpenAI docs explicitly show 4-image multi-ref examples on gpt-image-2.
  const [fusionProvider, setFusionProvider] = useState<'openai' | 'flux'>('openai');
  // For OpenAI: low|medium|high (quality tier). For FLUX: model ID.
  // Reset to provider-appropriate default when provider toggles (see effect below).
  const [fusionModel, setFusionModel] = useState<string>('low');
  const [fusionHeadline, setFusionHeadline] = useState('');
  const [fusionRunning, setFusionRunning] = useState(false);
  // 'single'         = BFL canonical pattern (product → 1 scene). Cleanest baseline.
  // 'fusion'         = multi-scene image-blend (the original variance hypothesis).
  // 'recipe-chunks'  = text-only chunks extracted per role from recipe sidecars,
  //                    single product image input. Sidesteps image-blend collision.
  //                    Requires role tags to do anything useful.
  // See backend/routes/dev.py docstrings for prompt shapes.
  const [fusionMode, setFusionMode] = useState<'single' | 'fusion' | 'recipe-chunks'>('fusion');

  // Reset model to provider-appropriate default whenever provider changes.
  useEffect(() => {
    setFusionModel(fusionProvider === 'openai' ? 'low' : 'flux-2-pro');
  }, [fusionProvider]);

  // Camera + Lighting control (Jun 26). 'auto' = open free-choice (but
  // gpt-image-2 is near-deterministic so identical prompts → identical
  // output). Specific values inject an instruction the model obeys → real
  // variance. Camera trimmed to the 3 that reliably read; lighting fills
  // the dropped mood-chunk gap (high visual impact).
  const [fusionCamera, setFusionCamera] = useState<string>('auto');
  const [fusionLighting, setFusionLighting] = useState<string>('auto');
  // Text-free scene (task #41) — render NO baked text + reserve negative
  // space so the JSON text overlay owns the copy. Default ON (overlay focus).
  const [fusionTextFree, setFusionTextFree] = useState(true);

  // Per-ref ROLE assignments (Jun 26 — "controlled chaos" mode).
  // Each selected recipe can be tagged with what to pull from it:
  // mood | background | composition | typography. When ANY role is set,
  // the prompt switches from "blend equally" to BFL-canonical "take
  // ROLE from image N" + explicit creative-placement license. Keyed
  // by recipe.id so unselecting + re-selecting keeps the prior role.
  type FusionRole = 'mood' | 'background' | 'composition' | 'typography';
  const [fusionRoles, setFusionRoles] = useState<Record<string, FusionRole | undefined>>({});
  const setRecipeRole = useCallback((recipeId: string, role: FusionRole | undefined) => {
    setFusionRoles((prev) => ({ ...prev, [recipeId]: role }));
  }, []);
  const clearAllRoles = useCallback(() => setFusionRoles({}), []);
  const [fusionResult, setFusionResult] = useState<{
    image_b64: string;
    cost: number;
    latency_s: number;
    provider: string;
    model: string;
    camera: string;
    lighting: string;
    references: Array<{ vibe_slug: string; id: string; preview_url?: string; role?: string | null }>;
    prompt: string;
    log_path: string | null;
  } | null>(null);
  const [fusionError, setFusionError] = useState<string | null>(null);

  // ─── Text-overlay preview (Jun 27) ───────────────────────────────────
  // After a fusion scene renders, composite a curated text template on top
  // (heading/subhead/cta over the scene) so the typography is good instead
  // of gpt-image-2's mush. This is the dev harness for the production
  // vision-picker (#39) + overlay engine (#40).
  const textTemplates = useMemo(() => getTextTemplateList(), []);
  const [overlayOn, setOverlayOn] = useState(false);
  const [overlayTemplateId, setOverlayTemplateId] = useState<string>(
    () => textTemplates[0]?.id ?? '',
  );
  const [overlayTone, setOverlayTone] = useState<OverlayTone>('auto');
  const [sceneLum, setSceneLum] = useState<number>(0.5);
  // Editable copy for the preview (production fills these from the concept).
  const [ovHeadline, setOvHeadline] = useState('Glow that lasts');
  const [ovSubhead, setOvSubhead] = useState('Clinically tested daily radiance serum');
  const [ovCta, setOvCta] = useState('Shop now');
  // Vision auto-picker (task #39, dev-only): gpt-4.1-mini reads the scene +
  // candidate layouts → picks template + tone + zone. Manual dropdowns still
  // override afterward.
  const [picking, setPicking] = useState(false);
  const [pickInfo, setPickInfo] = useState<{ zone: string; reason: string } | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  // Sample scene luminance whenever a fresh fusion result lands → drives
  // the 'auto' tone choice (dark scene → white text, light scene → dark).
  useEffect(() => {
    if (!fusionResult?.image_b64) return;
    let cancelled = false;
    void sampleSceneLuminance(fusionResult.image_b64).then((l) => {
      if (!cancelled) setSceneLum(l);
    });
    return () => {
      cancelled = true;
    };
  }, [fusionResult?.image_b64]);

  // Resolve the effective tone: explicit override, else luminance-derived.
  const effectiveTone: 'light' | 'dark' =
    overlayTone === 'auto' ? (sceneLum < 0.5 ? 'light' : 'dark') : overlayTone;

  const overlayTemplate = useMemo(() => {
    const base = overlayTemplateId ? getTextTemplate(overlayTemplateId) : undefined;
    return base ? applyTextTone(base, effectiveTone) : undefined;
  }, [overlayTemplateId, effectiveTone]);

  const overlaySlots: SocialPostSlots = useMemo(
    () => ({
      ...PLACEHOLDER_SLOTS,
      headline: ovHeadline,
      subhead: ovSubhead,
      cta: ovCta,
      product_name: productName.trim() || PLACEHOLDER_SLOTS.product_name,
    }),
    [ovHeadline, ovSubhead, ovCta, productName],
  );

  // Vision auto-pick: send the rendered scene + each template's text-layout
  // descriptor to gpt-4.1-mini, then apply the chosen template + tone.
  const handleAutoPick = useCallback(async () => {
    if (!fusionResult?.image_b64 || textTemplates.length === 0) return;
    setPicking(true);
    setPickError(null);
    try {
      const candidates = textTemplates.map((t) => {
        const full = getTextTemplate(t.id);
        return { id: t.id, name: t.name, layout: full ? describeTemplateLayout(full) : '' };
      });
      const resp = await fetch(`${API_BASE}/api/dev/pick-text-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scene_b64: fusionResult.image_b64, templates: candidates }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error((d as { detail?: string }).detail || `HTTP ${resp.status}`);
      }
      const data = (await resp.json()) as {
        template_id: string;
        tone: 'light' | 'dark';
        zone: string;
        reason: string;
      };
      setOverlayTemplateId(data.template_id);
      setOverlayTone(data.tone);
      setPickInfo({ zone: data.zone, reason: data.reason });
    } catch (e) {
      setPickError(e instanceof Error ? e.message : String(e));
    } finally {
      setPicking(false);
    }
  }, [fusionResult?.image_b64, textTemplates]);

  const FUSION_MAX = 4;
  const fusionRefCount = Math.min(selectedCount, FUSION_MAX);
  const fusionOverflow = selectedCount > FUSION_MAX;

  const handleRunFusion = useCallback(async () => {
    setFusionError(null);
    if (!productFile) {
      setFusionError('Upload a product image first.');
      return;
    }
    if (selectedCount === 0) {
      setFusionError('Select 1–4 recipes via the tile checkboxes.');
      return;
    }
    if (fusionOverflow) {
      setFusionError(`FLUX accepts max ${FUSION_MAX} references — deselect ${selectedCount - FUSION_MAX} tile(s).`);
      return;
    }

    // Take selection in the filteredFlat order so the refs go to the
    // provider in visible-grid order (predictable for repeat runs).
    // Attach per-recipe `role` from fusionRoles when set — when ANY ref
    // has a role, backend switches to the dimensional prompt mode.
    const recipes = filteredFlat
      .filter((r) => selectedIds.has(r.id))
      .slice(0, FUSION_MAX)
      .map((r) => ({
        vibe_slug: r.vibe_slug,
        id: r.id,
        role: fusionRoles[r.id] ?? undefined,
      }));

    // File → base64.
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read product file.'));
      reader.readAsDataURL(productFile);
    });
    const b64 = dataUrl.split(',', 2)[1] ?? '';
    if (!b64) {
      setFusionError('Could not encode product image.');
      return;
    }

    setFusionRunning(true);
    setFusionResult(null);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/flux-fusion-test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipe_ids: recipes,
          product_image_b64: b64,
          product_mime: productFile.type || 'image/png',
          product_name: productName.trim() || 'the product',
          headline: fusionHeadline.trim() || undefined,
          provider: fusionProvider,
          model: fusionModel,
          mode: fusionMode,
          camera: fusionCamera,
          lighting: fusionLighting,
          text_free: fusionTextFree,
          aspect_ratio: '1:1',
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${resp.status}: ${text || resp.statusText}`);
      }
      const data = await resp.json() as typeof fusionResult & { image_b64: string };
      setFusionResult(data);
    } catch (e: unknown) {
      setFusionError(e instanceof Error ? e.message : String(e));
    } finally {
      setFusionRunning(false);
    }
  }, [productFile, selectedCount, fusionOverflow, filteredFlat, selectedIds, productName, fusionHeadline, fusionModel, fusionMode, fusionProvider, fusionRoles, fusionCamera, fusionLighting, fusionTextFree]);

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface text-dark">
      {/* ─── Header ─── */}
      <header className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 px-5 py-3 flex items-center gap-3">
        <Link to="/moodboard-builder" className="text-xs text-primary hover:text-primary-hover">← Builder</Link>
        <div className="flex-1">
          <h1 className="text-base font-bold text-dark">Moodboard previews</h1>
          <p className="text-[11px] text-dark-muted">Bake preview thumbnails so the production picker can show recipes (not scraped moodboards).</p>
        </div>
      </header>

      {/* ─── Product upload bar ─── */}
      <section className="bg-white border-b border-surface-dark/40 px-5 py-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-surface-dark/50 bg-surface flex items-center justify-center overflow-hidden relative flex-shrink-0">
            {productPreviewUrl ? (
              <img src={productPreviewUrl} alt="product" className="w-full h-full object-contain" />
            ) : (
              <span className="text-[11px] text-dark-muted text-center px-2">Drop product</span>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleProductFile(e.target.files?.[0] ?? null)}
              className="absolute inset-0 opacity-0 cursor-pointer"
              aria-label="Upload product image"
            />
          </div>
          <div className="flex-1 min-w-[240px] grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="text-xs">
              <span className="block text-dark-muted mb-1">Product name (optional)</span>
              <input
                type="text"
                placeholder="amber dropper bottle"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="block w-full px-2 py-1.5 rounded-md border border-surface-dark/50 text-xs"
              />
            </label>
            <label className="text-xs">
              <span className="block text-dark-muted mb-1">Brand name (optional)</span>
              <input
                type="text"
                placeholder="Shop Rescue Spa"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="block w-full px-2 py-1.5 rounded-md border border-surface-dark/50 text-xs"
              />
            </label>
            {productFile && (
              <div className="sm:col-span-2 flex items-center gap-2 text-[11px] text-dark-muted">
                <span>{productFile.name} · {(productFile.size / 1024).toFixed(0)} KB</span>
                <button
                  type="button"
                  onClick={() => handleProductFile(null)}
                  className="text-primary hover:text-primary-hover"
                >Clear</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── Filter row ─── */}
      <section className="bg-white border-b border-surface-dark/40 px-5 py-3 flex items-center gap-3 flex-wrap">
        <FilterChip label="All vibes" active={vibeFilter === null} onClick={() => setVibeFilter(null)} />
        {VIBES.map((v) => (
          <FilterChip key={v} label={v} active={vibeFilter === v} onClick={() => setVibeFilter(v)} />
        ))}
        <span className="w-px h-5 bg-surface-dark/40 mx-1" />
        <FilterChip label="All types" active={productTypeFilter === null} onClick={() => setProductTypeFilter(null)} />
        {PRODUCT_TYPES.map((t) => (
          <FilterChip
            key={t}
            label={PRODUCT_TYPE_LABEL[t]}
            active={productTypeFilter === t}
            onClick={() => setProductTypeFilter(t)}
          />
        ))}
        <FilterChip
          label="⚠ Untagged"
          active={productTypeFilter === 'untagged'}
          onClick={() => setProductTypeFilter('untagged')}
        />
        <span className="w-px h-5 bg-surface-dark/40 mx-1" />
        <FilterChip label="All status" active={statusFilter === null} onClick={() => setStatusFilter(null)} />
        <FilterChip label="No preview" active={statusFilter === 'missing'} onClick={() => setStatusFilter('missing')} />
        <FilterChip label="Stale" active={statusFilter === 'stale'} onClick={() => setStatusFilter('stale')} />
        <FilterChip label="Fresh" active={statusFilter === 'fresh'} onClick={() => setStatusFilter('fresh')} />
        <span className="w-px h-5 bg-surface-dark/40 mx-1" />
        <FilterChip
          label={`📦 Product shot${productShotOnly ? ' ✓' : ''}`}
          active={productShotOnly}
          onClick={() => setProductShotOnly((v) => !v)}
        />
        <span className="w-px h-5 bg-surface-dark/40 mx-1" />
        <label className="flex items-center gap-1.5 text-xs text-dark-muted cursor-pointer">
          <input type="checkbox" checked={approvedOnly} onChange={(e) => setApprovedOnly(e.target.checked)} />
          Approved only
        </label>
        <div className="flex-1" />
        <button
          type="button"
          onClick={selectAllInView}
          disabled={filteredFlat.length === 0}
          className="px-2.5 py-1 rounded text-xs font-semibold text-primary hover:bg-primary-light disabled:opacity-40"
        >
          Select all in view ({filteredFlat.length})
        </button>
      </section>

      {/* ─── Multi-image fusion test panel (Jun 26 — variance-via-fusion experiment) ─── */}
      <section className="bg-gradient-to-r from-accent/5 to-primary/5 border-b border-accent/20 px-5 py-3">
        <button
          type="button"
          onClick={() => setFusionOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-bold text-accent hover:text-accent-hover"
        >
          <span>{fusionOpen ? '▼' : '▶'}</span>
          <span>⚡ Multi-Image Fusion Test</span>
          <span className="text-xs font-normal text-dark-muted">
            — recipe previews as references, no recipe spec sent to model
          </span>
        </button>

        {fusionOpen && (
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,420px)] gap-4">
            {/* Controls */}
            <div className="space-y-2 text-xs">
              <p className="text-dark-muted leading-relaxed">
                Pick 1–4 recipe tiles below via their checkboxes. Their
                baked previews + your uploaded product go to FLUX as
                multi-reference images. No recipe spec is sent — the
                images carry the aesthetic. Repeat runs with the same
                refs should produce <em>unique</em> interpolations (the
                hypothesis: variance via combination math, not directive).
              </p>
              <div className="flex items-center gap-3 flex-wrap pt-1">
                {/* Provider toggle — OpenAI (default, since our prod stack
                    already runs it) vs FLUX (cheaper at klein tier, faster
                    cold-start, but worse subject preservation in our Jun-26
                    fusion test). */}
                <div className="flex items-center gap-0 rounded-full border border-primary/30 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFusionProvider('openai')}
                    disabled={fusionRunning}
                    className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      fusionProvider === 'openai' ? 'bg-primary text-white' : 'bg-white text-dark-muted hover:bg-primary/10'
                    }`}
                    title="gpt-image-2 — already in our prod stack, mask-capable, better text rendering at med/high."
                  >OpenAI</button>
                  <button
                    type="button"
                    onClick={() => setFusionProvider('flux')}
                    disabled={fusionRunning}
                    className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      fusionProvider === 'flux' ? 'bg-primary text-white' : 'bg-white text-dark-muted hover:bg-primary/10'
                    }`}
                    title="FLUX 2 — cheap multi-ref, no mask, faster cold-start."
                  >FLUX</button>
                </div>

                {/* Mode toggle — three options. Single = BFL canonical
                    baseline (1 scene). Fusion = N-scene image blend.
                    Chunks = role-tagged TEXT extraction (no image blend
                    collision; needs roles set). */}
                <div className="flex items-center gap-0 rounded-full border border-accent/30 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFusionMode('single')}
                    disabled={fusionRunning}
                    className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      fusionMode === 'single' ? 'bg-accent text-white' : 'bg-white text-dark-muted hover:bg-accent/10'
                    }`}
                    title="Canonical BFL pattern: product + 1 scene. Cleanest baseline."
                  >Single-scene</button>
                  <button
                    type="button"
                    onClick={() => setFusionMode('fusion')}
                    disabled={fusionRunning}
                    className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      fusionMode === 'fusion' ? 'bg-accent text-white' : 'bg-white text-dark-muted hover:bg-accent/10'
                    }`}
                    title="Multi-scene image blend — N scene previews + product."
                  >Fusion</button>
                  <button
                    type="button"
                    onClick={() => setFusionMode('recipe-chunks')}
                    disabled={fusionRunning}
                    className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      fusionMode === 'recipe-chunks' ? 'bg-accent text-white' : 'bg-white text-dark-muted hover:bg-accent/10'
                    }`}
                    title="Text-only chunks extracted per role from recipe sidecars. No preview-image blend → no compositional collision. Needs roles assigned."
                  >Chunks</button>
                </div>

                <label className="flex items-center gap-1.5">
                  <span className="text-dark-muted">
                    {fusionProvider === 'openai' ? 'Quality' : 'Model'}
                  </span>
                  <select
                    value={fusionModel}
                    onChange={(e) => setFusionModel(e.target.value)}
                    disabled={fusionRunning}
                    className="px-2 py-1 rounded border border-surface-dark/50 text-xs"
                  >
                    {fusionProvider === 'openai' ? (
                      <>
                        <option value="low">low (~$0.025, 25-40s)</option>
                        <option value="medium">medium (~$0.05, 30-45s, better text)</option>
                        <option value="high">high (~$0.075, 60-90s, top text)</option>
                      </>
                    ) : (
                      <>
                        <option value="flux-2-pro">flux-2-pro ($0.03)</option>
                        <option value="flux-2-flex">flux-2-flex ($0.05, sharper text)</option>
                        <option value="flux-2-max">flux-2-max ($0.07, top quality)</option>
                        <option value="flux-2-klein-4b">flux-2-klein-4b ($0.014, fastest)</option>
                        <option value="flux-2-klein-9b">flux-2-klein-9b ($0.015)</option>
                      </>
                    )}
                  </select>
                </label>
                <label className="flex items-center gap-1.5">
                  <span className="text-dark-muted">Camera</span>
                  <select
                    value={fusionCamera}
                    onChange={(e) => setFusionCamera(e.target.value)}
                    disabled={fusionRunning}
                    className={`px-2 py-1 rounded border text-[11px] ${
                      fusionCamera !== 'auto' ? 'border-accent/60 bg-accent/5 font-semibold text-accent' : 'border-surface-dark/50 text-dark-muted'
                    }`}
                    title="Auto = model's free choice (same every run — gpt-image-2 is deterministic). Pick a specific angle to force variance."
                  >
                    <option value="auto">Auto (free choice)</option>
                    <option value="eye-level">Eye-level</option>
                    <option value="low-angle">Low-angle hero</option>
                    <option value="top-down">Top-down</option>
                  </select>
                </label>
                <label className="flex items-center gap-1.5">
                  <span className="text-dark-muted">Lighting</span>
                  <select
                    value={fusionLighting}
                    onChange={(e) => setFusionLighting(e.target.value)}
                    disabled={fusionRunning}
                    className={`px-2 py-1 rounded border text-[11px] ${
                      fusionLighting !== 'auto' ? 'border-accent/60 bg-accent/5 font-semibold text-accent' : 'border-surface-dark/50 text-dark-muted'
                    }`}
                    title="Fills the dropped mood-chunk gap. High visual impact — light direction + shadow depth changes the scene more than camera angle."
                  >
                    <option value="auto">Auto (free choice)</option>
                    <option value="soft-daylight">Soft daylight</option>
                    <option value="dramatic-side">Dramatic side-light</option>
                    <option value="golden-hour">Golden-hour backlight</option>
                    <option value="high-key">Bright high-key</option>
                    <option value="low-key">Moody low-key</option>
                  </select>
                </label>
                <label className="flex items-center gap-1.5 flex-1 min-w-[160px]">
                  <span className="text-dark-muted whitespace-nowrap">Headline</span>
                  <input
                    type="text"
                    placeholder={fusionTextFree ? '(text-free — overlay supplies copy)' : '(optional)'}
                    value={fusionHeadline}
                    onChange={(e) => setFusionHeadline(e.target.value)}
                    disabled={fusionRunning || fusionTextFree}
                    className={`flex-1 px-2 py-1 rounded border border-surface-dark/50 text-xs ${fusionTextFree ? 'opacity-40' : ''}`}
                  />
                </label>
                <label
                  className="flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                  title="Render the scene with NO baked text + reserved negative space, so the JSON overlay owns all copy (task #41)"
                >
                  <input
                    type="checkbox"
                    checked={fusionTextFree}
                    onChange={(e) => setFusionTextFree(e.target.checked)}
                    disabled={fusionRunning}
                  />
                  <span className="font-semibold text-accent">Text-free scene</span>
                </label>
              </div>
              {/* Generic-product-name warning. BFL docs are explicit:
                  "Be explicit about which element comes from which image."
                  Without a descriptive anchor, FLUX has nothing to grip — the
                  product gets diluted in the multi-ref blend (our Jun-26
                  first-test failure mode). */}
              {!productName.trim() && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠ Add a Product name above (e.g. "amber dropper bottle") — the model needs an explicit anchor or it dilutes the product into the scene blend.
                </p>
              )}

              {/* ─── Per-ref role assignments (controlled chaos) ───
                  Each selected ref can be tagged with WHICH dimension to
                  pull from it (mood / background / composition / typography).
                  When any role is set, backend switches to BFL-canonical
                  "take ROLE from image N" + explicit creative-placement
                  license. Without roles, falls back to the standard
                  fusion blend (safe baseline). */}
              {selectedCount > 0 && (
                <div className="pt-2 border-t border-accent/15">
                  <div className="flex items-center gap-2 pb-1">
                    <span className="text-[11px] font-bold text-dark">Per-ref roles</span>
                    <span className="text-[11px] text-dark-muted">
                      tag what to pull from each ref — leave blank for "blend all"
                    </span>
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={clearAllRoles}
                      disabled={fusionRunning || Object.keys(fusionRoles).length === 0}
                      className="text-[10px] text-primary hover:text-primary-hover disabled:opacity-30"
                    >Clear roles</button>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {filteredFlat
                      .filter((r) => selectedIds.has(r.id))
                      .slice(0, FUSION_MAX)
                      .map((r, idx) => {
                        const imageNum = idx + 2; // image 1 = product, refs start at 2
                        const role = fusionRoles[r.id];
                        return (
                          <div key={r.id} className="flex items-center gap-2 text-[11px]">
                            <span className="font-mono text-dark-muted w-14 shrink-0">image {imageNum}</span>
                            {r.preview_url ? (
                              <img src={r.preview_url} alt="" className="w-8 h-8 rounded object-cover shrink-0 border border-surface-dark/40" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-surface-dark/40 shrink-0" />
                            )}
                            <span className="text-dark truncate flex-1 min-w-0" title={`${r.vibe_slug}/${r.id}`}>
                              <span className="text-dark-muted">{r.vibe} · </span>
                              {r.id.length > 14 ? `…${r.id.slice(-12)}` : r.id}
                            </span>
                            <select
                              value={role ?? ''}
                              onChange={(e) => setRecipeRole(r.id, (e.target.value || undefined) as FusionRole | undefined)}
                              disabled={fusionRunning}
                              className={`px-2 py-1 rounded border text-[11px] ${
                                role ? 'border-accent/60 bg-accent/5 font-semibold text-accent' : 'border-surface-dark/50 text-dark-muted'
                              }`}
                            >
                              <option value="">— (any / blend)</option>
                              <option value="mood">🎨 Mood + lighting</option>
                              <option value="background">🖼 Background + props</option>
                              <option value="composition">📐 Composition + framing</option>
                              <option value="typography">🔤 Text layout + typography</option>
                            </select>
                          </div>
                        );
                      })}
                  </div>
                  {Object.values(fusionRoles).some((v) => !!v) && fusionMode !== 'recipe-chunks' && (
                    <p className="text-[10px] text-accent pt-1.5">
                      ✓ Dimensional mode active — each tagged ref contributes only its assigned axis (visual image-blend), and the model gets explicit license to compose freely.
                    </p>
                  )}
                  {fusionMode === 'recipe-chunks' && (
                    <p className="text-[10px] text-accent pt-1.5">
                      {Object.values(fusionRoles).some((v) => !!v)
                        ? '✓ Chunks mode active — each tagged ref contributes ONLY its role-specific recipe fields as TEXT. No preview-image blend (only product image sent → no compositional collision). Untagged refs are ignored.'
                        : '⚠ Chunks mode needs at least ONE role tag below. Untagged refs contribute nothing in this mode.'}
                    </p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 pt-1">
                <span className="text-dark-muted">
                  Refs: <strong className={fusionOverflow ? 'text-amber-700' : 'text-dark'}>
                    {fusionRefCount}
                  </strong> scene{fusionRefCount === 1 ? '' : 's'} + 1 product
                  {fusionOverflow && (
                    <span className="ml-2 text-amber-700">
                      (over cap — first {FUSION_MAX} in grid order will be used)
                    </span>
                  )}
                  {!productFile && <span className="ml-2 text-amber-700">— upload product above</span>}
                  {fusionMode === 'single' && fusionRefCount > 1 && (
                    <span className="ml-2 text-dark-muted italic">
                      (single-scene mode only uses the FIRST scene ref)
                    </span>
                  )}
                </span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => void handleRunFusion()}
                  disabled={fusionRunning || !productFile || selectedCount === 0}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold bg-accent text-white hover:bg-accent-hover disabled:opacity-50"
                >
                  {fusionRunning ? 'Rendering…' : 'Run Fusion'}
                </button>
              </div>
              {fusionError && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{fusionError}</p>
              )}
              {fusionResult && (
                <div className="text-[11px] text-dark-muted space-y-0.5 pt-1">
                  <div>
                    <span className="font-semibold text-dark">Provider:</span> {fusionResult.provider}
                    {' · '}
                    <span className="font-semibold text-dark">Model:</span> {fusionResult.model}
                    {' · '}
                    <span className="font-semibold text-dark">Camera:</span> {fusionResult.camera}
                    {' · '}
                    <span className="font-semibold text-dark">Lighting:</span> {fusionResult.lighting}
                    {' · '}
                    <span className="font-semibold text-dark">Cost:</span> ${fusionResult.cost.toFixed(4)}
                    {' · '}
                    <span className="font-semibold text-dark">Latency:</span> {fusionResult.latency_s}s
                  </div>
                  <div>
                    <span className="font-semibold text-dark">Refs:</span>{' '}
                    {fusionResult.references.map((r) => `${r.vibe_slug}/${r.id.slice(-8)}`).join(' + ')}
                  </div>
                  {fusionResult.log_path && (
                    <div className="font-mono break-all">
                      <span className="font-semibold text-dark not-italic">Log:</span> {fusionResult.log_path}
                    </div>
                  )}
                  <details className="pt-1">
                    <summary className="cursor-pointer text-dark-muted hover:text-dark">Prompt sent to FLUX</summary>
                    <pre className="mt-1 p-2 bg-white border border-surface-dark/30 rounded text-[10px] whitespace-pre-wrap break-words">{fusionResult.prompt}</pre>
                  </details>
                </div>
              )}
            </div>

            {/* Result image + source previews */}
            <div className="space-y-2">
              <div className="aspect-square rounded-lg border-2 border-dashed border-accent/30 bg-white overflow-hidden flex items-center justify-center">
                {fusionRunning ? (
                  <div className="flex flex-col items-center gap-2 text-xs text-dark-muted">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                    <span>
                      {fusionProvider === 'openai'
                        ? `Rendering on gpt-image-2 (${fusionModel}) — typically 25–60s`
                        : 'Rendering on FLUX — typically 10–15s'}
                    </span>
                  </div>
                ) : fusionResult ? (
                  <img
                    src={`data:image/png;base64,${fusionResult.image_b64}`}
                    alt="FLUX fusion result"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-dark-muted text-center px-4">
                    Result image appears here.<br />
                    Pick refs + product + Run.
                  </span>
                )}
              </div>

              {/* ── Text-overlay preview (Jun 27) ─────────────────────────
                  Composite a curated text template over the rendered scene
                  so typography is good (vs gpt-image-2's baked text). Dev
                  harness for the production vision-picker (#39) + overlay
                  engine (#40). */}
              {fusionResult && (
                <div className="rounded-lg border border-accent/30 bg-accent/[0.03] p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs font-bold text-accent uppercase tracking-wide cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overlayOn}
                        onChange={(e) => setOverlayOn(e.target.checked)}
                      />
                      Text overlay
                    </label>
                    {overlayOn && (
                      <span className="text-[10px] text-dark-muted">
                        scene luminance {sceneLum.toFixed(2)} → {effectiveTone} text
                      </span>
                    )}
                  </div>

                  {overlayOn && textTemplates.length === 0 && (
                    <p className="text-[11px] text-dark-muted">
                      No text templates yet. Author one in Figma (plugin → tick
                      “Text overlay” → Send to dev) and it lands in{' '}
                      <code>text-templates/</code>.
                    </p>
                  )}

                  {overlayOn && textTemplates.length > 0 && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        <label className="text-[11px] text-dark-muted flex items-center gap-1">
                          Template
                          <select
                            value={overlayTemplateId}
                            onChange={(e) => setOverlayTemplateId(e.target.value)}
                            className="border border-surface-dark/40 rounded px-1.5 py-0.5 text-[11px] bg-white"
                          >
                            {textTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.width}×{t.height})
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="text-[11px] text-dark-muted flex items-center gap-1">
                          Tone
                          <select
                            value={overlayTone}
                            onChange={(e) => setOverlayTone(e.target.value as OverlayTone)}
                            className="border border-surface-dark/40 rounded px-1.5 py-0.5 text-[11px] bg-white"
                          >
                            <option value="auto">Auto (luminance)</option>
                            <option value="light">Light text</option>
                            <option value="dark">Dark text</option>
                          </select>
                        </label>
                        <button
                          onClick={() => void handleAutoPick()}
                          disabled={picking}
                          title="gpt-4.1-mini reads the scene + candidate layouts and picks the best template + tone + zone"
                          className="px-2 py-0.5 rounded bg-accent text-white text-[11px] font-bold cursor-pointer disabled:opacity-50"
                        >
                          {picking ? 'Picking…' : '✨ Auto-pick (vision)'}
                        </button>
                      </div>
                      {pickInfo && (
                        <p className="text-[10px] text-accent leading-snug">
                          Vision → zone <b>{pickInfo.zone}</b>
                          {pickInfo.reason ? ` · ${pickInfo.reason}` : ''}
                        </p>
                      )}
                      {pickError && (
                        <p className="text-[10px] text-red-600">Auto-pick failed: {pickError}</p>
                      )}

                      <div className="grid grid-cols-1 gap-1.5">
                        <input
                          value={ovHeadline}
                          onChange={(e) => setOvHeadline(e.target.value)}
                          placeholder="Headline"
                          className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white"
                        />
                        <input
                          value={ovSubhead}
                          onChange={(e) => setOvSubhead(e.target.value)}
                          placeholder="Subhead"
                          className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white"
                        />
                        <input
                          value={ovCta}
                          onChange={(e) => setOvCta(e.target.value)}
                          placeholder="CTA"
                          className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white"
                        />
                      </div>

                      {/* Composited preview — scene as background, text on
                          top. Reuses JsonSocialPost via mergedSceneUrl. The
                          1080 canvas is scaled to a fixed 360px box. */}
                      {overlayTemplate && (
                        <div
                          className="relative mx-auto overflow-hidden rounded-md border border-surface-dark/40 bg-white"
                          style={{ width: 360, height: 360 }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              transform: `scale(${360 / (overlayTemplate.canvas?.width || 1080)})`,
                              transformOrigin: 'top left',
                            }}
                          >
                            <JsonSocialPost
                              config={overlayTemplate}
                              slots={overlaySlots}
                              productImageUrl=""
                              mergedSceneUrl={`data:image/png;base64,${fusionResult.image_b64}`}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Source recipe previews — which recipe fed each axis. Lets
                  the curator verify the breadcrumb is correct + compare the
                  source typography/background against the rendered result. */}
              {fusionResult && (() => {
                const bg = fusionResult.references.find((r) => r.role === 'background');
                const ty = fusionResult.references.find((r) => r.role === 'typography');
                if (!bg && !ty) return null;
                const Cell = ({ label, ref }: { label: string; ref?: { vibe_slug: string; id: string; preview_url?: string; role?: string | null } }) => (
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-accent uppercase tracking-wide mb-0.5">{label}</div>
                    {ref ? (
                      <>
                        <div className="aspect-square rounded-md overflow-hidden border border-surface-dark/40 bg-white">
                          {ref.preview_url ? (
                            <img src={ref.preview_url} alt={label} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-dark-muted">no preview baked</div>
                          )}
                        </div>
                        <div className="text-[10px] text-dark-muted truncate mt-0.5" title={`${ref.vibe_slug}/${ref.id}`}>
                          {ref.vibe_slug} · …{ref.id.slice(-8)}
                        </div>
                      </>
                    ) : (
                      <div className="aspect-square rounded-md border border-dashed border-surface-dark/40 flex items-center justify-center text-[10px] text-dark-muted">
                        not tagged
                      </div>
                    )}
                  </div>
                );
                return (
                  <div className="flex gap-2">
                    <Cell label="① Background recipe" ref={bg} />
                    <Cell label="② Text-layout recipe" ref={ty} />
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </section>

      {/* ─── Tile grid ─── */}
      <main className="px-5 py-4 pb-24">
        {loading && <p className="text-sm text-dark-muted">Loading…</p>}
        {loadError && <p className="text-sm text-red-600">Load error: {loadError}</p>}
        {!loading && filteredFlat.length === 0 && !loadError && (
          <p className="text-sm text-dark-muted">No recipes match the current filters.</p>
        )}
        {filteredFlat.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filteredFlat.map((rec) => (
              <PreviewTile
                key={rec.id}
                record={rec}
                selected={selectedIds.has(rec.id)}
                rendering={renderingIds.has(rec.id)}
                error={tileErrors[rec.id]}
                onToggle={() => toggleSelect(rec.id)}
                onSetProductShot={(v) => void setProductShot(rec, v)}
              />
            ))}
          </div>
        )}
      </main>

      {/* ─── Sticky action bar ─── */}
      {selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-surface-dark/40 shadow-lg px-5 py-3 flex items-center gap-3">
          <span className="text-sm font-semibold text-dark">
            {selectedCount} selected
            {selectedCount > SOFT_WARN && <span className="ml-2 text-amber-700">⚠ over {SOFT_WARN}</span>}
          </span>
          <span className="text-xs text-dark-muted">~${estimatedCost} · ~{Math.ceil(selectedCount / 3 * 30)}s</span>
          <div className="flex-1" />
          {batchSummary && !batchRunning && (
            <span className="text-xs text-emerald-700">
              Last batch: {batchSummary.succeeded} ok · {batchSummary.failed} failed · ${batchSummary.cost.toFixed(2)}
            </span>
          )}
          <button
            type="button"
            onClick={clearSelection}
            disabled={batchRunning}
            className="px-3 py-1.5 rounded-full text-xs text-dark-muted hover:bg-surface"
          >Clear</button>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={batchRunning || !productFile}
            className="px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {batchRunning ? `Generating ${renderingIds.size}…` : `Generate ${selectedCount} preview${selectedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-surface text-dark-muted hover:bg-surface-dark/40 hover:text-dark'
      }`}
    >{label}</button>
  );
}

function PreviewTile({
  record, selected, rendering, error, onToggle, onSetProductShot,
}: {
  record: MoodboardRecord;
  selected: boolean;
  rendering: boolean;
  error?: string;
  onToggle: () => void;
  onSetProductShot: (value: boolean) => void;
}) {
  const status = deriveStatus(record);
  // For curator UX, fall through to moodboard image if no preview yet —
  // this page is DEV-only so the third-party scraped image IS visible.
  // (Production picker uses preview_url only; placeholder otherwise.)
  const displayImage = record.preview_url ?? record.image_url;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={rendering}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
        selected
          ? 'border-primary shadow-md scale-[0.98]'
          : 'border-transparent hover:border-surface-dark/60'
      } ${rendering ? 'cursor-wait' : 'cursor-pointer'}`}
      title={`${record.id} · ${record.vibe}${record.product_types?.length ? ` · ${record.product_types.join(', ')}` : ''}`}
    >
      {displayImage ? (
        <img src={displayImage} alt={record.id} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-surface-dark/40 text-xs text-dark-muted">no image</div>
      )}

      {/* Render spinner overlay */}
      {rendering && (
        <div className="absolute inset-0 bg-dark/40 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Selection check */}
      {selected && !rendering && (
        <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shadow">✓</div>
      )}

      {/* Status badge top-left */}
      <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
        {status === 'fresh' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800">FRESH</span>}
        {status === 'stale' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800">STALE</span>}
        {status === 'missing' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-dark/80 text-dark">NO PREVIEW</span>}
      </div>

      {/* Moodboard ID chip — top-right under any selection check.
          Short ID (last 10 chars of the moodboard id) for compact display,
          with a copy button so the curator can grab the full id in one
          click. Bypasses the tile's parent onToggle so clicking the chip
          doesn't accidentally select/deselect. Jun 21 — added so /moodboard-
          previews can be used to spot-check classifier output on
          /moodboard-builder without opening the edit modal each time. */}
      {(() => {
        const fullId = `${record.vibe_slug}/${record.id}`;
        const shortId = record.id.length > 12
          ? `…${record.id.slice(-10)}`
          : record.id;
        return (
          <div
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              void navigator.clipboard?.writeText(fullId);
            }}
            title={`Click to copy ${fullId}`}
            className={`absolute ${selected && !rendering ? 'top-9' : 'top-1.5'} right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-white text-[10px] font-mono hover:bg-black/90 cursor-pointer`}
          >
            <i className="ri-file-copy-line text-[10px]" />
            <span>{shortId}</span>
          </div>
        );
      })()}

      {/* Vibe label bottom-left */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-dark/80 to-transparent px-2 py-1.5">
        <p className="text-[10px] text-white font-semibold truncate text-left">{record.vibe}</p>
        {record.product_types?.length ? (
          <p className="text-[9px] text-white/70 truncate text-left">{record.product_types.join(', ')}</p>
        ) : null}
      </div>

      {/* Product-shot tag toggle (Jun 23) — bottom-right, above the gradient.
          stopPropagation so clicking doesn't fire the tile's select-for-render
          action. Solid emerald when tagged, ghost when untagged. Tag the
          recipe AS A TEXT-FREE PRODUCT SHOT (no headline/CTA in the recipe
          spec — may include a model). The batch routes ~25% of slots here. */}
      <div
        role="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onSetProductShot(!record.is_product_shot);
        }}
        title={
          record.is_product_shot
            ? 'Tagged as text-free product shot — click to remove.'
            : 'Tag as text-free product shot (no headline/CTA in the recipe).'
        }
        className={`absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide shadow-sm cursor-pointer transition-colors ${
          record.is_product_shot
            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
            : 'bg-black/60 text-white/80 hover:bg-black/80'
        }`}
      >
        <span aria-hidden>📦</span>
        <span>{record.is_product_shot ? 'Product shot ✓' : 'Product shot'}</span>
      </div>

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 bg-red-600/80 text-white p-2 flex items-center justify-center text-[10px] text-center">{error}</div>
      )}
    </button>
  );
}
